import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useLedgerStats } from '@/api/hooks/useLedgerStats';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { Dialog } from '@/components/ui/Dialog';
import { KnowledgeLink } from '@/components/ui/KnowledgeLink';

interface StatsCardsProps {
    ledgerId: number;
    isAsset: boolean;
}

/**
 * Stats cards component displaying ledger performance metrics.
 *
 * Shows different cards for assets vs debts:
 * - Assets: P&L, MWRR, Current Value, Cash Flows
 * - Debts: Current Balance only
 */
export function StatsCards({ ledgerId, isAsset }: StatsCardsProps) {
    const { data: stats, isLoading, error } = useLedgerStats(ledgerId);
    const [currentValueHelpOpen, setCurrentValueHelpOpen] = useState(false);
    const [pnlHelpOpen, setPnlHelpOpen] = useState(false);
    const [mwrrHelpOpen, setMwrrHelpOpen] = useState(false);

    if (isLoading) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-white dark:bg-secondary rounded-lg p-4 shadow-xs border border-gray-200 dark:border-gray-700">
                        <div className="animate-pulse">
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-sm w-16 mb-2" />
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-sm w-24" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (error || !stats) {
        return null; // Don't show anything if stats fail to load
    }

    // For debt ledgers, show minimal stats
    if (stats.isDebt || !isAsset) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <StatsCard
                    label="Current Balance"
                    value={<MoneyDisplay value={stats.currentValue} size="lg" />}
                />
            </div>
        );
    }

    // For asset ledgers, show full stats
    return (
        <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {/* Current Value & Cash Flows - full width on mobile, 1/3 on desktop */}
                <div className="col-span-2 md:col-span-1">
                    <StatsCard
                        label="Current Value"
                        value={
                            <div className="flex flex-col gap-2">
                                <MoneyDisplay value={stats.currentValue} size="lg" />
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500 dark:text-gray-400">In:</span>
                                        <MoneyDisplay value={stats.totalInflows} size="sm" />
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500 dark:text-gray-400">Out:</span>
                                        <MoneyDisplay value={stats.totalOutflows} size="sm" />
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500 dark:text-gray-400">Net:</span>
                                        <MoneyDisplay value={stats.netCashflow} size="sm" colorize />
                                    </div>
                                </div>
                            </div>
                        }
                        onInfoClick={() => setCurrentValueHelpOpen(true)}
                    />
                </div>
                <StatsCard
                    label="Net P&L"
                    value={
                        stats.netProfit != null ? (
                            <MoneyDisplay value={stats.netProfit} size="lg" colorize />
                        ) : (
                            <span className="text-lg font-semibold text-gray-500 dark:text-gray-400">N/A</span>
                        )
                    }
                    subtitle={
                        stats.netProfit != null
                            ? (stats.gainPercentage != null ? (
                                <span className={stats.gainPercentage >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                    {stats.gainPercentage >= 0 ? '+' : ''}{stats.gainPercentage.toFixed(2)}%
                                </span>
                              ) : undefined)
                            : 'Requires cash flow history'
                    }
                    onInfoClick={() => setPnlHelpOpen(true)}
                />
                <StatsCard
                    label="MWRR"
                    value={
                        stats.mwrr != null ? (
                            <span className={`text-lg font-semibold ${stats.mwrr >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {stats.mwrr >= 0 ? '+' : ''}{stats.mwrr.toFixed(2)}%
                            </span>
                        ) : (
                            <span className="text-lg font-semibold text-gray-500 dark:text-gray-400">N/A</span>
                        )
                    }
                    subtitle={stats.mwrr != null ? 'Annualized Return' : 'Requires 3+ months of data'}
                    onInfoClick={() => setMwrrHelpOpen(true)}
                />
            </div>

            {/* P&L Help Dialog */}
            <Dialog
                open={pnlHelpOpen}
                onClose={() => setPnlHelpOpen(false)}
                title="About P&L"
                size="sm"
            >
                <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                    <p>
                        <strong className="text-gray-900 dark:text-white">P&L (Profit & Loss)</strong> shows your total
                        gain or loss for this asset.
                    </p>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Period:</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            From your first deposit to today.
                        </p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">The percentage shows your gain rate:</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            ((Current Value + Withdrawals - Deposits) / Deposits) x 100
                        </p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Example:</p>
                        <ul className="list-disc list-inside space-y-0.5 text-gray-500 dark:text-gray-400">
                            <li>Deposited: 10,000</li>
                            <li>Withdrew: 2,000</li>
                            <li>Current value: 12,000</li>
                            <li>P&L: 4,000 (+40%)</li>
                        </ul>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">
                        This measures how much your investment has grown relative to what you put in,
                        accounting for any withdrawals.
                    </p>
                    <p className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <KnowledgeLink page="pnl">Learn more about P&L</KnowledgeLink>
                    </p>
                </div>
            </Dialog>

            {/* MWRR Help Dialog */}
            <Dialog
                open={mwrrHelpOpen}
                onClose={() => setMwrrHelpOpen(false)}
                title="About MWRR"
                size="sm"
            >
                <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                    <p>
                        <strong className="text-gray-900 dark:text-white">MWRR (Money-Weighted Rate of Return)</strong> is
                        your annualized return that accounts for the timing and size of your cash flows.
                    </p>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Period:</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            From your first deposit to today, annualized to a yearly rate.
                        </p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Why use MWRR?</p>
                        <ul className="list-disc list-inside space-y-0.5 text-gray-500 dark:text-gray-400">
                            <li>Reflects the actual return on your invested capital</li>
                            <li>Accounts for when you added or withdrew money</li>
                            <li>Comparable across different time periods</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Example:</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            If you invested 1,000 in January and 10,000 in December, and the asset grew 10% early
                            in the year but stayed flat later, MWRR will be lower than the P&L percentage because
                            most of your money missed the growth period.
                        </p>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">
                        MWRR requires at least 3 months of data history to provide meaningful results.
                        This avoids showing volatile or misleading returns for new investments.
                    </p>
                    <p className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <KnowledgeLink page="mwrr">Learn more about MWRR</KnowledgeLink>
                    </p>
                </div>
            </Dialog>

            {/* Current Value Help Dialog */}
            <Dialog
                open={currentValueHelpOpen}
                onClose={() => setCurrentValueHelpOpen(false)}
                title="About Current Value"
                size="sm"
            >
                <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                    <p>
                        <strong className="text-gray-900 dark:text-white">Current Value</strong> shows
                        the latest recorded value of this asset.
                    </p>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Cash Flow Summary:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-500 dark:text-gray-400">
                            <li><strong>In:</strong> Total deposits and contributions made</li>
                            <li><strong>Out:</strong> Total withdrawals taken</li>
                            <li><strong>Net:</strong> Net contribution (<strong>In</strong> minus <strong>Out</strong>)</li>
                        </ul>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">
                        A positive Net means you've contributed more than withdrawn.
                        A negative Net means you've withdrawn more than contributed.
                    </p>
                    <p className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <KnowledgeLink page="value">Learn more about Value</KnowledgeLink>
                    </p>
                </div>
            </Dialog>
        </>
    );
}

interface StatsCardProps {
    label: string;
    value: React.ReactNode;
    subtitle?: React.ReactNode;
    onInfoClick?: () => void;
}

/**
 * Individual stats card component.
 */
function StatsCard({ label, value, subtitle, onInfoClick }: StatsCardProps) {
    return (
        <div className="bg-white dark:bg-secondary rounded-lg p-4 shadow-xs border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {label}
                </p>
                {onInfoClick && (
                    <button
                        type="button"
                        onClick={onInfoClick}
                        className="p-0.5 text-gray-400 hover:text-accent transition-colors"
                        title={`About ${label}`}
                    >
                        <InformationCircleIcon className="h-4 w-4" />
                    </button>
                )}
            </div>
            <div className="text-gray-900 dark:text-white">
                {value}
            </div>
            {subtitle && (
                <p className="text-xs mt-1">
                    {subtitle}
                </p>
            )}
        </div>
    );
}
